import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdPersonSearch } from 'react-icons/md';
import api, { usuariosAPI } from '../services/api';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.5rem; }

  &:hover {
    background-color: ${p => p.theme.colors.background};
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};

  svg { color: ${p => p.theme.colors.primary}; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};

  span.required { color: ${p => p.theme.colors.danger}; }
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 100px;
  resize: vertical;
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background-color: ${p => p.primary ? p.theme.colors.primary : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.25rem; }

  &:hover {
    background-color: ${p => p.primary ? p.theme.colors.primaryDark : p.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${p => p.theme.shadows.sm};
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const ErrorMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  margin: ${p => p.theme.spacing.md} 0 0;
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 3px solid ${p => p.theme.colors.danger};
`;

const NewLead = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [form, setForm] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    origen: 'web',
    estado: 'nuevo',
    fecha_primer_contacto: '',
    interes_inicial: '',
    notas: '',
    asignado_a: '',
  });

  useEffect(() => {
    usuariosAPI.getAll().then(r => setUsuarios(r.data.data || [])).catch(() => {});
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        fecha_primer_contacto: form.fecha_primer_contacto || null,
      };
      await api.post('/leads', payload);
      navigate('/leads');
    } catch (err) {
      setError(err.message || 'Error al guardar el lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}>
          <MdArrowBack />
        </BackButton>
        <Title>Nuevo Lead</Title>
      </PageHeader>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <SectionTitle>
            <MdPersonSearch />
            Informacion del Prospecto
          </SectionTitle>

          <FormGrid>
            <FormGroup>
              <Label>Nombre <span className="required">*</span></Label>
              <Input
                type="text"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                placeholder="Nombre completo"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Empresa</Label>
              <Input
                type="text"
                name="empresa"
                value={form.empresa}
                onChange={handleChange}
                placeholder="Nombre de la empresa"
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="correo@ejemplo.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>Telefono</Label>
              <Input
                type="text"
                name="telefono"
                value={form.telefono}
                onChange={handleChange}
                placeholder="+52 000 000 0000"
              />
            </FormGroup>

            <FormGroup>
              <Label>Origen</Label>
              <Select name="origen" value={form.origen} onChange={handleChange}>
                <option value="web">Web</option>
                <option value="referido">Referido</option>
                <option value="llamada">Llamada</option>
                <option value="expo">Expo</option>
                <option value="redes_sociales">Redes Sociales</option>
                <option value="otro">Otro</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Estado</Label>
              <Select name="estado" value={form.estado} onChange={handleChange}>
                <option value="nuevo">Nuevo</option>
                <option value="contactado">Contactado</option>
                <option value="calificado">Calificado</option>
                <option value="descartado">Descartado</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Fecha de Primer Contacto</Label>
              <Input
                type="date"
                name="fecha_primer_contacto"
                value={form.fecha_primer_contacto}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Asignar a</Label>
              <Select name="asignado_a" value={form.asignado_a} onChange={handleChange}>
                <option value="">Sin asignar</option>
                {usuarios.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>

          <FormGrid style={{ gridTemplateColumns: '1fr' }}>
            <FormGroup>
              <Label>Interes Inicial</Label>
              <TextArea
                name="interes_inicial"
                value={form.interes_inicial}
                onChange={handleChange}
                placeholder="Describe el interes o necesidad del prospecto..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Notas</Label>
              <TextArea
                name="notas"
                value={form.notas}
                onChange={handleChange}
                placeholder="Observaciones adicionales..."
              />
            </FormGroup>
          </FormGrid>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <ButtonGroup>
            <Btn type="button" onClick={() => navigate(-1)} disabled={submitting}>
              Cancelar
            </Btn>
            <Btn type="submit" primary disabled={submitting}>
              <MdSave />
              {submitting ? 'Guardando...' : 'Guardar Lead'}
            </Btn>
          </ButtonGroup>
        </form>
      </FormCard>
    </Container>
  );
};

export default NewLead;
